import type {
  ApiMetric,
  ComponentPerfMetric,
  DeviceType,
  ErrorMetric,
  LongTaskMetric,
  MemoryMetric,
  MetricsBatchPayload,
  MetricsConfig,
  NavigationMetric,
  NetworkMetric,
  PageLoadMetric,
  ResourceMetric,
  WebVitalMetric,
  WebVitalName,
} from './types'
import { getToken } from '@/utils/request/token'

declare const __RELEASE_VERSION__: string
declare const __BUILD_ID__: string
declare const __FRONTEND_ENV__: string

/** 默认配置 */
const DEFAULT_CONFIG: MetricsConfig = {
  enabled: true,
  sampleRate: parseFloat(import.meta.env['VITE_METRICS_SAMPLE_RATE'] || '0.3'),
  batchInterval: 30000,
  maxBatchSize: 150,
  endpoint: `${import.meta.env.VITE_API_BASE_URL || ''}/common/metrics/web-vitals/`,
  debug: import.meta.env['VITE_METRICS_DEBUG'] === 'true',
}

/** UA 解析结果 */
interface UAInfo {
  browser: string
  browserVersion: string
  os: string
  osVersion: string
}

/** 诊断快照原始数据（供问题反馈使用，仅包含非隐私的排查信息） */
export interface ReporterDiagnosticsData {
  /** 性能监控会话 ID */
  sessionId: string
  /** 前端发布版本 */
  release: string
  /** 浏览器名称与主版本 */
  browser: string
  /** 操作系统与大版本 */
  os: string
  /** 设备类型 */
  device: DeviceType
  /** 最近错误（最多 10 条） */
  recentErrors: ErrorMetric[]
  /** 每个 Core Web Vital 的最新值 */
  webVitals: WebVitalMetric[]
}

/** 轻量 UA 解析，提取浏览器和操作系统信息 */
function parseUserAgent(): UAInfo {
  const ua = navigator.userAgent
  let browser = 'Other'
  let browserVersion = ''
  let os = 'Other'
  let osVersion = ''

  let match: RegExpMatchArray | null

  match = ua.match(/Edg\/(\d+)/)
  if (match) {
    browser = 'Edge'
    browserVersion = match[1] ?? ''
  } else if ((match = ua.match(/Chrome\/(\d+)/))) {
    browser = 'Chrome'
    browserVersion = match[1] ?? ''
  } else if ((match = ua.match(/Firefox\/(\d+)/))) {
    browser = 'Firefox'
    browserVersion = match[1] ?? ''
  } else if ((match = ua.match(/Version\/(\d+).*Safari/))) {
    browser = 'Safari'
    browserVersion = match[1] ?? ''
  }

  if ((match = ua.match(/Windows NT ([\d.]+)/))) {
    os = 'Windows'
    osVersion = match[1] ?? ''
  } else if ((match = ua.match(/Mac OS X ([\d_]+)/))) {
    os = 'macOS'
    osVersion = (match[1] ?? '').replace(/_/g, '.').split('.')[0] ?? ''
  } else if ((match = ua.match(/Android ([\d.]+)/))) {
    os = 'Android'
    osVersion = (match[1] ?? '').split('.')[0] ?? ''
  } else if ((match = ua.match(/(?:iPhone|iPad) OS ([\d_]+)/))) {
    os = 'iOS'
    osVersion = (match[1] ?? '').replace(/_/g, '.').split('.')[0] ?? ''
  } else if (/Linux/.test(ua)) {
    os = 'Linux'
    osVersion = ''
  }

  return { browser, browserVersion, os, osVersion }
}

/** 生成会话 ID */
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** 检测设备类型 */
function detectDeviceType(): DeviceType {
  const ua = navigator.userAgent
  if (/Mobi|Android.*Mobile|iPhone|iPod/.test(ua)) return 'mobile'
  if (/iPad|Android(?!.*Mobile)|Tablet/.test(ua)) return 'tablet'
  return 'desktop'
}

/** 获取网络连接类型 */
function getConnectionType(): string {
  return navigator.connection?.effectiveType || 'unknown'
}

/**
 * 性能指标上报器（单例）
 *
 * 负责缓冲各类指标并定时批量发送到后端
 */
class MetricsReporter {
  private config: MetricsConfig
  private sessionId: string
  private deviceType: DeviceType
  private uaInfo: UAInfo
  private sampled: boolean
  private webVitals: WebVitalMetric[] = []
  private navigations: NavigationMetric[] = []
  private apiMetrics: ApiMetric[] = []
  private pageLoads: PageLoadMetric[] = []
  private longTasks: LongTaskMetric[] = []
  private resources: ResourceMetric[] = []
  private errors: ErrorMetric[] = []
  /** 最近错误（不随批量上报清空，供诊断快照读取） */
  private recentErrors: ErrorMetric[] = []
  /** 每个 Core Web Vital 的最新值（不随批量上报清空） */
  private latestWebVitals = new Map<WebVitalName, WebVitalMetric>()
  private memorySnapshots: MemoryMetric[] = []
  private networkSnapshots: NetworkMetric[] = []
  private componentPerfs: ComponentPerfMetric[] = []
  private flushTimer: ReturnType<typeof setInterval> | null = null

  constructor(config: Partial<MetricsConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.sessionId = generateSessionId()
    this.deviceType = detectDeviceType()
    this.uaInfo = parseUserAgent()
    this.sampled = Math.random() < this.config.sampleRate

    if (!this.sampled || !this.config.enabled) return

    this.flushTimer = setInterval(() => this.flush(), this.config.batchInterval)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  /** 当前会话是否被采样 */
  get isSampled(): boolean {
    return this.sampled
  }

  /** 页面隐藏时立即 flush */
  private onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.flush()
    }
  }

  /** 添加 Web Vital 指标 */
  addWebVital(metric: WebVitalMetric): void {
    // 诊断快照需要最新指标，即使当前会话未被采样也保留
    this.latestWebVitals.set(metric.name, metric)
    if (!this.sampled) return
    this.webVitals.push(metric)
    this.checkBatchSize()
  }

  /** 添加路由切换指标 */
  addNavigation(metric: NavigationMetric): void {
    if (!this.sampled) return
    this.navigations.push(metric)
    this.checkBatchSize()
  }

  /** 添加 API 性能指标 */
  addApiMetric(metric: ApiMetric): void {
    if (!this.sampled) return
    this.apiMetrics.push(metric)
    this.checkBatchSize()
  }

  /** 添加页面加载指标 */
  addPageLoad(metric: PageLoadMetric): void {
    if (!this.sampled) return
    this.pageLoads.push(metric)
  }

  /** 添加长任务指标 */
  addLongTask(metric: LongTaskMetric): void {
    if (!this.sampled) return
    this.longTasks.push(metric)
    if (this.longTasks.length > 20) {
      this.longTasks.sort((a, b) => b.duration - a.duration)
      this.longTasks = this.longTasks.slice(0, 20)
    }
    this.checkBatchSize()
  }

  /** 添加资源加载指标 */
  addResource(metric: ResourceMetric): void {
    if (!this.sampled) return
    this.resources.push(metric)
    if (this.resources.length > 30) {
      this.resources.sort((a, b) => b.duration - a.duration)
      this.resources = this.resources.slice(0, 30)
    }
    this.checkBatchSize()
  }

  /** 添加错误指标 */
  addError(metric: ErrorMetric): void {
    // 诊断快照需要最近错误，即使当前会话未被采样也保留
    this.rememberRecentError(metric)
    if (!this.sampled) return
    const isDuplicate = this.errors.some(
      e => e.message === metric.message && e.source === metric.source && e.lineno === metric.lineno
    )
    if (isDuplicate) return
    if (this.errors.length >= 10) return
    this.errors.push(metric)
    this.checkBatchSize()
  }

  /** 记录最近错误（最多 10 条，按 message / source / lineno 去重） */
  private rememberRecentError(metric: ErrorMetric): void {
    const isDuplicate = this.recentErrors.some(
      error =>
        error.message === metric.message &&
        error.source === metric.source &&
        error.lineno === metric.lineno
    )
    if (isDuplicate) return
    this.recentErrors.push({ ...metric })
    if (this.recentErrors.length > 10) {
      this.recentErrors.shift()
    }
  }

  /** 添加内存快照 */
  addMemory(metric: MemoryMetric): void {
    if (!this.sampled) return
    this.memorySnapshots.push(metric)
  }

  /** 添加网络质量快照 */
  addNetwork(metric: NetworkMetric): void {
    if (!this.sampled) return
    this.networkSnapshots.push(metric)
  }

  /** 添加组件渲染指标 */
  addComponentPerf(metric: ComponentPerfMetric): void {
    if (!this.sampled) return
    this.componentPerfs.push(metric)
    if (this.componentPerfs.length > 20) {
      this.componentPerfs.sort((a, b) => b.duration - a.duration)
      this.componentPerfs = this.componentPerfs.slice(0, 20)
    }
    this.checkBatchSize()
  }

  /** 缓冲区满时强制 flush */
  private checkBatchSize(): void {
    const total =
      this.webVitals.length +
      this.navigations.length +
      this.apiMetrics.length +
      this.longTasks.length +
      this.resources.length +
      this.errors.length +
      this.componentPerfs.length
    if (total >= this.config.maxBatchSize) {
      this.flush()
    }
  }

  /** 判断所有缓冲是否为空 */
  private isEmpty(): boolean {
    return (
      this.webVitals.length === 0 &&
      this.navigations.length === 0 &&
      this.apiMetrics.length === 0 &&
      this.pageLoads.length === 0 &&
      this.longTasks.length === 0 &&
      this.resources.length === 0 &&
      this.errors.length === 0 &&
      this.memorySnapshots.length === 0 &&
      this.networkSnapshots.length === 0 &&
      this.componentPerfs.length === 0
    )
  }

  /** 批量发送指标到后端 */
  flush(): void {
    if (!this.sampled) return
    if (this.isEmpty()) return

    const payload: MetricsBatchPayload = {
      sessionId: this.sessionId,
      timestamp: Date.now(),
      pagePath: location.pathname,
      userAgentType: this.deviceType,
      connectionType: getConnectionType(),
      releaseVersion: typeof __RELEASE_VERSION__ !== 'undefined' ? __RELEASE_VERSION__ : '',
      frontendEnv: typeof __FRONTEND_ENV__ !== 'undefined' ? __FRONTEND_ENV__ : '',
      buildId: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '',
      browser: this.uaInfo.browser,
      browserVersion: this.uaInfo.browserVersion,
      os: this.uaInfo.os,
      osVersion: this.uaInfo.osVersion,
      webVitals: [...this.webVitals],
      navigations: [...this.navigations],
      apiMetrics: [...this.apiMetrics],
      pageLoads: [...this.pageLoads],
      longTasks: [...this.longTasks],
      resources: [...this.resources],
      errors: [...this.errors],
      memorySnapshots: [...this.memorySnapshots],
      networkSnapshots: [...this.networkSnapshots],
      componentPerfs: [...this.componentPerfs],
    }

    this.webVitals = []
    this.navigations = []
    this.apiMetrics = []
    this.pageLoads = []
    this.longTasks = []
    this.resources = []
    this.errors = []
    this.memorySnapshots = []
    this.networkSnapshots = []
    this.componentPerfs = []

    if (this.config.debug) {
      // eslint-disable-next-line no-console
      console.groupCollapsed('[Metrics] flush')
      // eslint-disable-next-line no-console
      if (payload.webVitals.length) console.table(payload.webVitals)
      // eslint-disable-next-line no-console
      if (payload.navigations.length) console.table(payload.navigations)
      // eslint-disable-next-line no-console
      if (payload.apiMetrics.length) console.table(payload.apiMetrics.slice(0, 10))
      // eslint-disable-next-line no-console
      if (payload.pageLoads.length) console.table(payload.pageLoads)
      // eslint-disable-next-line no-console
      if (payload.longTasks.length) console.table(payload.longTasks)
      // eslint-disable-next-line no-console
      if (payload.resources.length) console.table(payload.resources)
      // eslint-disable-next-line no-console
      if (payload.errors.length) console.table(payload.errors)
      // eslint-disable-next-line no-console
      if (payload.memorySnapshots.length) console.table(payload.memorySnapshots)
      // eslint-disable-next-line no-console
      if (payload.networkSnapshots.length) console.table(payload.networkSnapshots)
      // eslint-disable-next-line no-console
      if (payload.componentPerfs.length) console.table(payload.componentPerfs)
      // eslint-disable-next-line no-console
      console.groupEnd()
      return
    }

    this.send(payload)
  }

  /** 通过 fetch keepalive 发送数据 */
  private send(payload: MetricsBatchPayload): void {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    try {
      fetch(this.config.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
      })
    } catch {
      // 静默失败，不影响用户体验
    }
  }

  /** 获取诊断快照原始数据（不包含表单内容、账号密码等隐私字段） */
  getDiagnosticsData(): ReporterDiagnosticsData {
    const browser = `${this.uaInfo.browser} ${this.uaInfo.browserVersion}`.trim()
    const os = `${this.uaInfo.os} ${this.uaInfo.osVersion}`.trim()
    const webVitalOrder: WebVitalName[] = ['LCP', 'CLS', 'INP', 'FCP', 'TTFB']
    const webVitals = webVitalOrder.flatMap(name => {
      const metric = this.latestWebVitals.get(name)
      return metric ? [{ ...metric }] : []
    })

    return {
      sessionId: this.sessionId,
      release: typeof __RELEASE_VERSION__ !== 'undefined' ? __RELEASE_VERSION__ : '',
      browser: browser || 'Unknown',
      os: os || 'Unknown',
      device: this.deviceType,
      recentErrors: this.recentErrors.map(error => ({ ...error })),
      webVitals,
    }
  }

  /** 销毁 reporter，清理定时器和事件监听 */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }
}

/** 全局 reporter 实例 */
let reporterInstance: MetricsReporter | null = null

/** 初始化 reporter（仅首次调用生效） */
export function initReporter(config?: Partial<MetricsConfig>): MetricsReporter {
  if (!reporterInstance) {
    reporterInstance = new MetricsReporter(config)
  }
  return reporterInstance
}

/** 获取当前 reporter 实例 */
export function getReporter(): MetricsReporter | null {
  return reporterInstance
}
